import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { isCarouselDto, isLinkServiceDetailsDto } from '@hcr/api/optimizely'
import { COLORS, ICONS, LinkUnstyled, unit } from '@hcr/ui'
import { flow, getPropertyValue, group, isNotNull, isNotUndefined, sort } from '@hcr/utils'

import { useHoliday } from '../../../../contexts'
import {
  useAccommodationDetailsQuery,
  useDestinationDetailsQuery,
  useIdToken,
  useRoomDetailsQuery,
} from '../../../../hooks'
import { useDestinationsQuery, useServicesQuery } from '../../../../hooks/api/optimizely/queries'
import { LocaleNamespace, Path } from '../../../../models'
import { createLinkGoogleMaps, createRoomNameString, isOwnerUsage, to, compareStringsIntl } from '../../../../utils'
import { WarningMissingData } from '../../warning-missing-data'
import {
  BookingDates,
  LayoutBookingOverview,
  LayoutBookingOverviewContent,
  LayoutBookingOverviewDivider,
  LayoutBookingOverviewHeader,
  LayoutBookingOverviewSection,
} from '../common'
import { RecommendSpaTile } from '../common/recommend-spa-tile/RecommendSpaTile'

import { BookingOverviewLoading } from './BookingOverviewLoading'

const SPA_CATEGORY_ID = 'spa'
const SPA_SECTIONS = ['Kylpylä ja hoidot', 'Kylpylä']
const SPA_TITLE = 'Kylpylä'

export const BookingOverview: FC = () => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const holiday = useHoliday()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    { enabled: isNotNull(idToken) && isNotNull(holiday.data.accommodationId) }
  )

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(holiday.data.destinationId) },
    { enabled: isNotNull(holiday.data.destinationId) }
  )

  const destinations = useDestinationsQuery({
    select: flow(
      getPropertyValue('destinations'),
      sort({ comparer: compareStringsIntl, asc: getPropertyValue('title') }),
      group(({ isSpa }) => (isSpa ? 'isSpa' : 'isNotSpa'))
    ),
  })

  const services = useServicesQuery(
    { destinationId: String(holiday.data.destinationId) },
    { enabled: isNotNull(holiday.data.destinationId) }
  )

  const roomDetails = useRoomDetailsQuery(
    { destinationId: String(holiday.data.destinationId), roomId: String(accommodation.data?.room_type_id) },
    { enabled: isNotNull(holiday.data.destinationId) && isNotUndefined(accommodation.data?.room_type_id) }
  )

  const getServiceId = (): string | undefined => {
    const serviceGroup = services?.data?.content
      ?.filter(isCarouselDto)
      .find(component => SPA_SECTIONS.includes(component.title))
    const serviceLink = serviceGroup?.content?.find(service => service.title === SPA_TITLE)?.link
    return serviceLink && isLinkServiceDetailsDto(serviceLink) ? serviceLink.serviceId : undefined
  }

  if (
    holiday.isSuccess &&
    accommodation.isSuccess &&
    services.isSuccess &&
    destinations.isSuccess &&
    destinationDetails.isSuccess
  ) {
    const destinationWithSpa = destinations.data.get('isSpa')
    const isDestinationWithSpa = destinationWithSpa?.find(
      destination => destination.destinationId === destinationDetails.data.destinationId
    )
    const isReservationWithSpaTicket = !!accommodation.data.package_tickets.find(
      ticket => ticket.category_id === SPA_CATEGORY_ID
    )
    const serviceId: string | undefined = getServiceId()

    const destinationDetailsLink = destinationDetails.isSuccess
      ? createLinkGoogleMaps({
          label: destinationDetails.data.address,
          target: 'external',
          url: destinationDetails.data.address,
        })
      : null

    return (
      <Stack gap={unit(7)}>
        {(destinationDetails.isError || roomDetails.isError || accommodation.isError) && (
          <Box marginBottom={unit(1.5)}>
            <WarningMissingData />
          </Box>
        )}
        <LayoutBookingOverview>
          <Typography visibility='hidden' component='h1' sx={{ height: unit(0) }} fontWeight='initial'>
            {destinationDetails.data?.name}
          </Typography>

          <LayoutBookingOverviewHeader gap={unit(2)}>
            <Typography variant='bodyM'>{t('booking-overview.your-booking-at')}</Typography>

            <Box
              component='img'
              alt={destinationDetails.data?.name}
              src={destinationDetails.data?.logoUrl}
              width='auto'
              height={unit(9)}
              visibility={destinationDetails.isSuccess ? 'visible' : 'hidden'}
            />
          </LayoutBookingOverviewHeader>
          <LayoutBookingOverviewContent>
            <BookingDates
              checkInDate={accommodation.data.start_date}
              checkInHour={roomDetails.data?.checkInHour}
              checkOutDate={accommodation.data.end_date}
              checkOutHour={roomDetails.data?.checkOutHour}
            />
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.accommodation')}>
              <Typography variant='bodyM'>
                {roomDetails.isSuccess ? createRoomNameString(roomDetails.data) : accommodation.data.room_type}
              </Typography>
              <Icons>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farMoonStars} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.data.days}
                  </Typography>
                </IconWithLabel>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farFamilyPants} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.data.adults + accommodation.data.children}
                  </Typography>
                </IconWithLabel>
              </Icons>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.booking-status')}>
              <Typography variant='bodyM'>
                {t(accommodation.data.status, { ns: LocaleNamespace.AccommodationStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewSection label={t('booking-overview.payment-status')}>
              <Typography variant='bodyM'>
                {isOwnerUsage(accommodation.data)
                  ? t('booking-overview.owner-payment')
                  : t(accommodation.data.payment_status, { ns: LocaleNamespace.PaymentStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            {destinationDetails.isSuccess && destinationDetailsLink && (
              <LayoutBookingOverviewSection label={t('booking-overview.arrival-info')}>
                <Typography
                  component='a'
                  href={String(destinationDetailsLink.to)}
                  key={destinationDetailsLink.label}
                  target='_blank'
                  rel='external'
                  variant='textLinkM'
                  color={COLORS.linknega[500]}
                >
                  {destinationDetailsLink.label}
                  <FontAwesomeIcon style={{ marginLeft: unit(2) }} icon={ICONS.fasLocationArrow} />
                </Typography>
              </LayoutBookingOverviewSection>
            )}
            <Button component={LinkUnstyled} to={to(Path.BookingDetails)} variant='outlined' color='white'>
              {t('booking-overview.see-booking-details')}
            </Button>
          </LayoutBookingOverviewContent>
        </LayoutBookingOverview>
        {!isReservationWithSpaTicket && isDestinationWithSpa && serviceId && destinationDetails?.data && (
          <RecommendSpaTile destinationId={destinationDetails.data.destinationId} serviceId={serviceId} />
        )}
      </Stack>
    )
  }

  return <BookingOverviewLoading />
}

const Icons = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(5.5)};
  margin-top: ${unit(1)};
`

const IconWithLabel = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(1.5)};
`
